/**
 * Application Constants
 * All application-wide constant values
 */

/**
 * Text-based extensions that are read verbatim via `file.text()` (no
 * format-specific parser). This is the single source of truth for the
 * "is this a plain-text file" decision in fileParser, the admin allow-list
 * defaults, and the file-input `accept` attribute. Add new text/data/config
 * /source-code formats here and every consumer picks them up automatically.
 * All entries are lowercase and dot-prefixed.
 */
export const TEXT_EXTENSIONS = [
  // Plain text & markup
  '.txt', '.md', '.markdown', '.rst', '.tex', '.log',
  // Structured data
  '.csv', '.tsv', '.json', '.jsonl', '.ndjson', '.xml', '.yaml', '.yml',
  '.html', '.htm',
  // Config
  '.ini', '.cfg', '.conf', '.toml', '.env', '.properties',
  // Source code & scripts
  '.js', '.mjs', '.cjs', '.jsx', '.ts', '.tsx', '.py', '.rb', '.php',
  '.java', '.kt', '.go', '.rs', '.c', '.h', '.cpp', '.hpp', '.cc', '.cs',
  '.swift', '.scala', '.sh', '.bash', '.zsh', '.ps1', '.sql', '.css',
  '.scss', '.less',
] as const;

/**
 * Image formats Azure OpenAI vision models can decode (GPT-5, GPT-4o, GPT-4.1).
 * GIF is supported as a static frame only — animation is ignored by the model.
 */
export const IMAGE_EXTENSIONS = [
  '.png',
  '.jpg',
  '.jpeg',
  '.webp',
  '.gif',
] as const;

/**
 * Common image formats the model CANNOT decode. Used to surface a tailored
 * "convert to JPEG/PNG" rejection instead of a generic "unsupported type".
 */
export const UNSUPPORTED_IMAGE_EXTENSIONS = [
  '.heic',
  '.heif',
  '.bmp',
  '.tif',
  '.tiff',
  '.svg',
] as const;

// File upload constraints
export const FILE_CONSTRAINTS = {
  MAX_FILE_SIZE: 100 * 1024 * 1024, // 100MB in bytes (used as default when admin hasn't configured uploadLimits)
  MAX_FILES: 5,
  SUPPORTED_TYPES: {
    PDF: 'application/pdf',
    DOCX: 'application/vnd.openxmlformats-officedocument.' +
          'wordprocessingml.document',
    XLSX: 'application/vnd.openxmlformats-officedocument.' +
          'spreadsheetml.sheet',
    TXT: 'text/plain',
    MD: 'text/markdown',
  },
  // Binary formats with dedicated parsers, followed by every text-based
  // extension (see TEXT_EXTENSIONS). Drives the admin allow-list checkboxes.
  SUPPORTED_EXTENSIONS: ['.pdf', '.docx', '.xlsx', ...TEXT_EXTENSIONS],
} as const;

// API configuration
export const API_CONFIG = {
  GRAPH_SEARCH_SIZE: 10,
  GRAPH_SEARCH_MAX_SIZE: 50,
  OPENAI_MAX_TOKENS: 4000,  // Increased from 1000 for longer responses
  // Fallback input context window (in tokens) used to budget the chat request
  // when an agent does not declare `contextWindowTokens`. Conservative on
  // purpose: safe for 128K-window deployments (e.g. gpt-4o) with margin, and
  // never overflows larger windows — agents on bigger models (gpt-4.1, gpt-5,
  // 272K+) should set their real window per-agent to use full capacity.
  DEFAULT_CONTEXT_WINDOW_TOKENS: 120000,
  OPENAI_TEMPERATURE: 0.7,
  OPENAI_TOP_P: 0.95,
  REQUEST_TIMEOUT: 30000, // 30 seconds
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000, // 1 second
} as const;

// Local storage keys
export const STORAGE_KEYS = {
  CONVERSATIONS: 'stellar_conversations',
  CURRENT_CONVERSATION_ID: 'stellar_current_conversation',
  USER_PREFERENCES: 'stellar_user_preferences',
  VERSION: 'stellar_storage_version',
} as const;

// Storage version for data migration
export const STORAGE_VERSION = '1.0.0';

// Message limits
export const MESSAGE_LIMITS = {
  MAX_CONTENT_LENGTH: 2000,
  MAX_CONVERSATION_HISTORY: 20,
  MAX_CONTEXT_LENGTH: 100000, // 100KB context for modern models (GPT-5 supports 128k+ tokens)
} as const;

// Date grouping
export const DATE_GROUPS = {
  TODAY: 'Today',
  YESTERDAY: 'Yesterday',
  LAST_7_DAYS: 'Last 7 days',
  LAST_30_DAYS: 'Last 30 days',
  OLDER: 'Older',
} as const;

// System prompts
//
// Citation labels are namespaced per source so they never collide when the
// same turn pulls from multiple retrievers:
//   - Microsoft 365 (Graph)        -> [M365 N]
//   - Azure AI Search (index docs) -> [Doc N]
//   - Web search                   -> [Web N]
// CITATION_INSTRUCTIONS is shared between the default-assistant path and the
// custom-agent path so retrieval behavior stays consistent regardless of
// whether an agent overrides the system prompt.
/**
 * @deprecated Use `buildCitationInstructions` from
 * `@/utils/citationInstructions` instead. This export is retained only
 * as a back-compat fallback in `openAIService.ts` for callers that
 * haven't migrated to passing `activeSources`.
 */
export const CITATION_INSTRUCTIONS = `Reference material may include Microsoft 365 documents (cited as [M365 N]), Azure AI Search results ([Doc N]), and web search results ([Web N]).

How to use it:
- Weave information from these sources into a single, coherent answer. Do not narrate the retrieval ("based on the web search...", "the documents say...") — just answer the question directly.
- Cite inline using the exact label immediately after the specific fact it supports, e.g. "Stellar acquired Atlas in 2024 [Web 2]." When multiple sources back the same point, list them together: [M365 1][Web 3].
- Use markdown structure (short headings, bullet lists) when the answer has multiple parts; keep short answers conversational.
- Only cite when stating a specific fact from a source. Do not invent citations and do not cite for general knowledge.
- If the references don't cover the question, say so plainly and answer from general knowledge if appropriate.`;

export const SYSTEM_PROMPTS = {
  DEFAULT: `You are a helpful AI assistant for Stellar. Answer clearly and concisely. If you don't know something, say so rather than guessing.`,

  /**
   * @deprecated The dynamic prompt assembly in `openAIService.ts` no
   * longer calls this. Kept only so existing unit tests that exercise
   * the shape of the function continue to compile.
   */
  WITH_CONTEXT: (context: string) => `You are a helpful AI assistant for Stellar.

${CITATION_INSTRUCTIONS}

${context}`,
} as const;

// Error messages
export const ERROR_MESSAGES = {
  AUTH_FAILED: 'Authentication failed. Please try again.',
  TOKEN_EXPIRED: 'Your session has expired. Please log in again.',
  NETWORK_ERROR: 'Network error. Please check your connection.',
  FILE_TOO_LARGE: (maxMB: number = FILE_CONSTRAINTS.MAX_FILE_SIZE / 1024 / 1024) =>
    `File size exceeds ${maxMB}MB limit.`,
  INVALID_FILE_TYPE: 'Unsupported file type.',
  TOO_MANY_FILES: `Maximum ${FILE_CONSTRAINTS.MAX_FILES} files allowed.`,
  GRAPH_API_ERROR: 'Failed to search Microsoft 365 content.',
  OPENAI_API_ERROR: 'Failed to get AI response.',
  STORAGE_FULL: 'Local storage is full. Please delete old conversations.',
  GENERIC_ERROR: 'Something went wrong. Please try again.',

  // NEW: Enhanced error messages for specific scenarios
  RATE_LIMIT: 'Rate limit exceeded. Please wait before sending another message.',
  AUTH_ERROR: 'Authentication failed. Please check your credentials.',
  SERVICE_ERROR: 'The AI service is temporarily unavailable.',
  TIMEOUT_ERROR: 'Request timed out. Please try again.',
  CONTENT_FILTERED: 'Response was filtered by content policy.',
  RESPONSE_TRUNCATED: 'Response was truncated due to token limits.',
} as const;

// Success messages
export const SUCCESS_MESSAGES = {
  FILE_UPLOADED: 'File uploaded successfully',
  CONVERSATION_SAVED: 'Conversation saved',
  CONVERSATION_DELETED: 'Conversation deleted',
  CONVERSATION_EXPORTED: 'Conversation exported',
} as const;

// Animation durations (in ms)
export const ANIMATIONS = {
  FAST: 150,
  BASE: 200,
  SLOW: 300,
} as const;

// Keyboard shortcuts
export const KEYBOARD_SHORTCUTS = {
  SEND_MESSAGE: 'Enter',
  NEW_LINE: 'Shift+Enter',
  NEW_CONVERSATION: 'Ctrl+N',
  SEARCH: 'Ctrl+K',
  CLOSE_MODAL: 'Escape',
} as const;
